/**
 * PRODUCT CARD - Quantity-based Variant Switcher for Homepage/Collection Pages
 * Automatically switches variants and updates prices based on quantity
 * Shows discount badges for tiered pricing
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  
  const VARIANT_RULES = window.variantRules || {};
  const VARIANT_RULES_ADVANCED = window.variantRulesAdvanced || {};
  const VARIANT_BASE_PRICES = window.variantBasePrices || {};
  const DISCOUNT_THRESHOLD = 40; // Quantity threshold for discount

  // Discount tier messages
  const DISCOUNT_MESSAGES = {
    tier2: '%Discount', // 40-99 units
    tier3: '%Discount'  // 100+ units
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Get variant ID for quantity (simple mode)
   */
  function getVariantIdForQuantity(productId, quantity) {
    const rules = VARIANT_RULES[productId];
    if (!rules) return null;

    const thresholds = Object.keys(rules)
      .map(Number)
      .sort((a, b) => b - a);

    for (let threshold of thresholds) {
      if (quantity >= threshold) {
        return rules[threshold];
      }
    }
    return null;
  }

  /**
   * Get variant ID for quantity (advanced mode with options)
   */
  function getVariantIdForQuantityAdvanced(productId, optionValue, quantity) {
    const rules = VARIANT_RULES_ADVANCED[productId];
    if (!rules) return null;

    const optionRules = rules[optionValue];
    if (!optionRules) return null;

    const thresholds = Object.keys(optionRules)
      .map(Number)
      .sort((a, b) => b - a);

    for (let threshold of thresholds) {
      if (quantity >= threshold) {
        return optionRules[threshold];
      }
    }
    return null;
  }

  /**
   * Fetch variant data from Shopify API
   */
  async function fetchVariantData(variantId) {
    try {
      const response = await fetch(`/products.json?limit=250`);
      const data = await response.json();
      
      for (let product of data.products) {
        const variant = product.variants.find(v => v.id.toString() === variantId.toString());
        if (variant) {
          return {
            variant: variant,
            product: product
          };
        }
      }
    } catch (error) {
      console.error('[Product Card Switcher] Error fetching variant data:', error);
    }
    return null;
  }

  /**
   * Format price in EUR
   */
  function formatPrice(price) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }

  /**
   * Calculate discount percentage
   */
  function calculateDiscountPercentage(originalPrice, discountedPrice) {
    if (!originalPrice || !discountedPrice) return 0;
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
  }

  /**
   * Update price display with strikethrough
   */
  function updatePriceDisplay(card, variantData, quantity, basePrice) {
    const priceWrapper = card.querySelector('.product-card__price-wrapper');
    if (!priceWrapper) return;

    const priceElement = priceWrapper.querySelector('.price');
    if (!priceElement) return;

    const currentPriceElement = priceElement.querySelector('.price-item--regular');
    if (!currentPriceElement) return;

    // Update current price
    const variantPrice = variantData.variant.price / 100;
    currentPriceElement.textContent = formatPrice(variantPrice);

    // Handle strikethrough price
    let comparePriceElement = priceElement.querySelector('.price-item--compare');
    
    if (quantity >= DISCOUNT_THRESHOLD && basePrice) {
      // Show strikethrough for discounted prices
      if (!comparePriceElement) {
        comparePriceElement = document.createElement('s');
        comparePriceElement.className = 'price-item price-item--compare';
        comparePriceElement.style.marginRight = '8px';
        comparePriceElement.style.color = '#999';
        priceElement.insertBefore(comparePriceElement, currentPriceElement);
      }
      comparePriceElement.textContent = formatPrice(basePrice);
      comparePriceElement.style.display = '';
    } else {
      // Remove strikethrough for non-discounted prices
      if (comparePriceElement) {
        comparePriceElement.style.display = 'none';
      }
    }
  }

  /**
   * Update discount badge
   */
  function updateDiscountBadge(card, quantity, originalPrice, discountedPrice) {
    const badge = card.querySelector('.discount-badge');
    if (!badge) return;

    if (quantity >= DISCOUNT_THRESHOLD && originalPrice && discountedPrice) {
      const discountPercent = calculateDiscountPercentage(originalPrice, discountedPrice);
      const badgeText = badge.querySelector('.discount-text');
      
      if (discountPercent > 0) {
        let message = '';
        if (quantity >= 100) {
          message = DISCOUNT_MESSAGES.tier3.replace('%', discountPercent);
        } else if (quantity >= 40) {
          message = DISCOUNT_MESSAGES.tier2.replace('%', discountPercent);
        }
        
        badgeText.textContent = message;
        badge.style.display = 'block';
      } else {
        badge.style.display = 'none';
      }
    } else {
      badge.style.display = 'none';
    }
  }

  /**
   * Handle quantity change for a product card
   */
  async function handleQuantityChange(card, quantityInput) {
    const productId = quantityInput.dataset.productId;
    const quantity = parseInt(quantityInput.value) || 1;

    console.log('[Product Card Switcher] Product:', productId, 'Quantity:', quantity);

    // Check if product has variant rules
    const hasSimpleRules = VARIANT_RULES[productId];
    const hasAdvancedRules = VARIANT_RULES_ADVANCED[productId];

    if (!hasSimpleRules && !hasAdvancedRules) {
      console.log('[Product Card Switcher] No rules for product:', productId);
      return;
    }

    let newVariantId = null;
    let selectedOption = null;

    // For now, we'll use simple rules (can be extended for advanced rules later)
    if (hasAdvancedRules) {
      // Get first option value from the first variant
      const currentVariantId = quantityInput.dataset.variantId;
      const variantData = await fetchVariantData(currentVariantId);
      
      if (variantData && variantData.variant.options && variantData.variant.options[0]) {
        selectedOption = variantData.variant.options[0];
        newVariantId = getVariantIdForQuantityAdvanced(productId, selectedOption, quantity);
      }
    } else if (hasSimpleRules) {
      newVariantId = getVariantIdForQuantity(productId, quantity);
    }

    if (!newVariantId) {
      console.log('[Product Card Switcher] No variant found for quantity:', quantity);
      return;
    }

    // Fetch new variant data
    const newVariantData = await fetchVariantData(newVariantId);
    if (!newVariantData) {
      console.error('[Product Card Switcher] Could not fetch variant data');
      return;
    }

    // Get base price for strikethrough
    let basePrice = null;
    if (VARIANT_BASE_PRICES[productId]) {
      if (selectedOption && VARIANT_BASE_PRICES[productId][selectedOption]) {
        basePrice = VARIANT_BASE_PRICES[productId][selectedOption];
      } else if (typeof VARIANT_BASE_PRICES[productId] === 'number') {
        basePrice = VARIANT_BASE_PRICES[productId];
      }
    }

    // Update price display
    updatePriceDisplay(card, newVariantData, quantity, basePrice);

    // Update discount badge
    const discountedPrice = newVariantData.variant.price / 100;
    updateDiscountBadge(card, quantity, basePrice, discountedPrice);

    // Update variant ID in input
    quantityInput.dataset.variantId = newVariantId;

    console.log('[Product Card Switcher] ✓ Updated to variant:', newVariantId);
  }

  /**
   * Initialize quantity controls for a card
   */
  function initializeCard(card) {
    const quantityInput = card.querySelector('.quantity-input');
    if (!quantityInput) return;

    const minusBtn = card.querySelector('.quantity-btn--minus');
    const plusBtn = card.querySelector('.quantity-btn--plus');
    
    // Get minimum quantity from data attribute or input min attribute
    const minQty = parseInt(quantityInput.dataset.min) || parseInt(quantityInput.getAttribute('min')) || 1;
    const step = parseInt(quantityInput.getAttribute('step')) || 1;

    // Handle input change
    quantityInput.addEventListener('input', function() {
      let value = parseInt(this.value) || minQty;
      if (value < minQty) value = minQty;
      this.value = value;
      handleQuantityChange(card, this);
    });

    quantityInput.addEventListener('change', function() {
      let value = parseInt(this.value) || minQty;
      if (value < minQty) value = minQty;
      this.value = value;
      handleQuantityChange(card, this);
    });

    // Handle minus button
    if (minusBtn) {
      minusBtn.addEventListener('click', function() {
        let value = parseInt(quantityInput.value) || minQty;
        if (value > minQty) {
          value = Math.max(minQty, value - step);
          quantityInput.value = value;
          handleQuantityChange(card, quantityInput);
        }
      });
    }

    // Handle plus button
    if (plusBtn) {
      plusBtn.addEventListener('click', function() {
        let value = parseInt(quantityInput.value) || minQty;
        value += step;
        quantityInput.value = value;
        handleQuantityChange(card, quantityInput);
      });
    }

    // Initial check
    handleQuantityChange(card, quantityInput);

    console.log('[Product Card Switcher] Initialized card with min qty:', minQty, 'step:', step);
  }

  /**
   * Initialize all product cards on the page
   */
  function initializeAllCards() {
    const cards = document.querySelectorAll('.product-card__quantity-wrapper');
    
    if (cards.length === 0) {
      console.log('[Product Card Switcher] No product cards with quantity inputs found');
      return;
    }

    cards.forEach(wrapper => {
      const card = wrapper.closest('.product-card');
      if (card) {
        initializeCard(card);
      }
    });

    console.log('[Product Card Switcher] Initialized', cards.length, 'cards');
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize when DOM is ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeAllCards);
    } else {
      initializeAllCards();
    }

    // Re-initialize when new content is loaded
    document.addEventListener('shopify:section:load', initializeAllCards);
  }

  // Start the script
  init();

  // Expose for debugging
  window.ProductCardVariantSwitcher = {
    init: initializeAllCards
  };

})();
